import { Injectable } from '@angular/core';
import { v4 as uuid } from 'uuid';
import { DprConsumableDetail } from '@shared/models/dpr';
import { DprConsumableDetailGridData } from '@dpr-details/models/grid';
import { Consumable } from '@shared/models/project-setup';
import { Sync } from '@shared/models/grid';
import { DailyStockService } from '../daily-stock.service';

@Injectable()
export class DprConsumableGridService {
  constructor(private dailyStockService: DailyStockService) {}

  buildGridData(
    consumableList: Consumable[],
    dprConsumableDetails: DprConsumableDetail[]
  ): DprConsumableDetailGridData[] {
    const data = [];

    consumableList.forEach((item) => {
      const dprConsumableDetail = dprConsumableDetails.find(
        (d) => d.consumableId === item.id
      );

      const entity: DprConsumableDetailGridData = {
        id: uuid(),
        dprConsumableId: null,
        priority: item.priority,
        name: `${item.name} ${item.units ? '[' + item.units + ']' : ''}`,
        previous: 0,
        today: null,
        dailyStockDetails: {},
        total: 0,
        comment: null,
        consumableId: item.id,
      };

      if (dprConsumableDetail) {
        entity.id = dprConsumableDetail.id ?? entity.id;
        entity.dprConsumableId = dprConsumableDetail.dprConsumableId;
        entity.previous = dprConsumableDetail.previous;
        entity.today = dprConsumableDetail.today;
        entity.added = dprConsumableDetail.added;
        entity.used = dprConsumableDetail.used;
        entity.total = this.calculateTotal(
          dprConsumableDetail.previous,
          dprConsumableDetail.today
        );

        // needed for calculations
        entity.dailyStockDetails = { ...dprConsumableDetail.dailyStockDetails };

        // flatten the dailyStockDetails to be able to use it in the grid
        entity.dailyStockDetailsTotalStartOfDay =
          dprConsumableDetail.dailyStockDetails?.totalStartOfDay;
        entity.dailyStockDetailsTotalEndOfDay =
          dprConsumableDetail.dailyStockDetails?.totalEndOfDay;
        entity.dailyStockDetailsTotalUsed =
          dprConsumableDetail.dailyStockDetails?.totalUsed;

        entity.comment = dprConsumableDetail.comment;
      }

      data.push(entity);
    });

    return data;
  }

  mapToGridSync(
    dprConsumableId: number | string,
    changes: Sync<DprConsumableDetailGridData>
  ): Sync<DprConsumableDetail> {
    const result = {
      addedList: [],
      updatedList: [],
      deletedIds: changes?.deletedIds ?? [],
    };

    const mappingFunction = (data: DprConsumableDetailGridData) => ({
      id: data.id,
      dprConsumableId,
      consumableId: data.consumableId,
      previous: data.previous,
      today: data.today,
      added: data.added,
      used: data.used,
      comment: data.comment,
      dailyStockDetails: this.dailyStockService.buildDailyStockDetails(data),
    });

    result.addedList.push(...(changes?.addedList?.map(mappingFunction) ?? [])); // needed for offline
    result.addedList.push(
      ...(changes?.updatedList
        ?.filter((x) => isNaN(+x.id))
        .map(mappingFunction) ?? [])
    );

    result.updatedList = changes?.updatedList
      ?.filter((x) => !isNaN(+x.id))
      .map(mappingFunction);

    return result;
  }

  shouldTriggerAutoFill(
    data: DprConsumableDetailGridData[],
    enableStock: boolean
  ): boolean {
    return enableStock
      ? this.dailyStockService.shouldTriggerAutoFillOfStock(data)
      : data.some((reg) => reg.today != null) &&
          data.some((reg) => reg.today == null);
  }

  calculateTotal(previous: number, today: number): number {
    return previous + (today ?? 0);
  }
}
